/**
 * Maps API route paths to human-readable endpoint names.
 * Used in admin dashboard tables and charts to display friendly names
 * instead of raw API paths.
 */

const ROUTE_NAME_MAP: Record<string, string> = {
  // ── Search Service ──────────────────────────────────────────
  '/api/search/text': 'Text Search',
  '/api/search/validate': 'Query Validation',
  '/api/search/text/source': 'Source-Specific Text Search',
  '/api/search/image': 'Image Search',
  '/api/search/hybrid': 'Hybrid Search',
  '/api/search/suggestions': 'Search Suggestions',
  '/api/search/public': 'Public Search',
  '/api/search/history': 'Search History',

  // Vehicles
  '/api/search/vehicles/makes': 'Vehicle Makes',
  '/api/search/vehicles/models': 'Vehicle Models',
  '/api/search/vehicles/variants': 'Vehicle Variants',
  '/api/search/vehicles/fuel-types': 'Vehicle Fuel Types',
  '/api/search/vehicles/variants/fast': 'Fast Variant Lookup',
  '/api/search/vehicles/fuel-types/fast': 'Fast Fuel Type Lookup',
  '/api/search/vehicles/sub-aggregates': 'Sub-Aggregates',

  // Search Admin
  '/api/search/admin/logs': 'Search Admin Logs',

  // Admin (Search Service)
  '/api/admin/health': 'Health Check',
  '/api/admin/logs': 'Admin Logs',
  '/api/admin/analytics': 'Admin Analytics',
  '/api/admin/cache/stats': 'Cache Statistics',
  '/api/admin/cache/clear': 'Clear Cache',
  '/api/admin/services/status': 'Service Status',
  '/api/admin/metrics': 'Metrics',
  '/api/admin/circuit-breakers': 'Circuit Breakers',
  '/api/admin/circuit-breakers/reset': 'Reset Circuit Breakers',
  '/api/admin/usage': 'Usage Statistics',

  // Bulk Search
  '/api/search/bulk': 'Bulk Search',
  '/api/search/bulk/upload': 'Bulk Search Upload',

  // ASR (Speech-to-Text)
  '/api/asr/transcribe': 'Voice Transcription',
  '/api/asr': 'Voice Search',
  '/api/asr/health': 'ASR Health Check',

  // API v1
  '/api/v1/search/text': 'Text Search (v1)',
  '/api/v1/search/history': 'Search History (v1)',
  '/api/v1/search/suggestions': 'Search Suggestions (v1)',
  '/api/v1/vehicles/makes': 'Vehicle Makes (v1)',
  '/api/v1/vehicles/models': 'Vehicle Models (v1)',
  '/api/v1/vehicles/variants': 'Vehicle Variants (v1)',
  '/api/v1/vehicles/fuel-types': 'Vehicle Fuel Types (v1)',
  '/api/v1/vehicles/sub-aggregates': 'Sub-Aggregates (v1)',

  // ── Client Management Service ───────────────────────────────
  '/api/auth/login': 'Admin Login',
  '/api/auth/refresh': 'Token Refresh',
  '/api/auth/logout': 'Logout',
  '/api/auth/verify': 'Token Verification',

  // Tenants
  '/api/tenant/onboard': 'Tenant Onboarding',
  '/api/tenant': 'Tenant List',

  // Clients (Legacy)
  '/api/client/onboard': 'Client Onboarding',
  '/api/client': 'Client List',

  // Admin - Vehicles
  '/api/admin/vehicles': 'Vehicle Management',

  // Admin - Scopes
  '/api/admin/scopes': 'Available Scopes',

  // Admin - Pending Actions
  '/api/admin/pending-actions': 'Pending Actions',

  // Config
  '/api/config': 'Configuration',

  // ── Vector Search Service ───────────────────────────────────
  '/api/a9/suggestions': 'Autocomplete Suggestions',
  '/api/a9/popularity': 'Popularity Tracking',
  '/api/a9/sync': 'Autocomplete Sync',
  '/api/a9/sync/mode': 'Sync Mode Update',
  '/api/a9/sync/status': 'Sync Status',
  '/api/a9/sync/cleanup': 'Sync Cleanup',
  '/api/a9/backend/status': 'Autocomplete Backend Status',

  '/api/vector/search': 'Vector Similarity Search',

  '/api/embedding/embed': 'Generate Embedding',
  '/api/embedding/embed/batch': 'Batch Embedding',
  '/api/embedding/status': 'Embedding Model Status',
  '/api/embedding/reload': 'Reload Embedding Model',

  // ── External Catalog Service ────────────────────────────────
  '/api/catalog/search/tvs': 'TVS Catalog Search',
  '/api/catalog/search/boodmo': 'Boodmo Catalog Search',
  '/api/catalog/vehicle/validate': 'Vehicle Validation',
  '/api/catalog/catfullrecords/search': 'CatFullRecords Search',
  '/api/catalog/catfullrecords/makes': 'Catalog Makes',
  '/api/catalog/catfullrecords/models': 'Catalog Models',
  '/api/catalog/catfullrecords/variants': 'Catalog Variants',
  '/api/catalog/catfullrecords/variants-with-parts': 'Catalog Variants with Parts',
  '/api/catalog/catfullrecords/fuel-types': 'Catalog Fuel Types',

  // ── Image Processing Service ────────────────────────────────
  '/api/upload/image': 'Image Upload',
  '/api/upload/hybrid': 'Hybrid Upload',
  '/api/upload/history': 'Upload History',

  // ── Analytics Service ───────────────────────────────────────
  '/api/analytics/log': 'Log Event',
  '/api/analytics/stats/search': 'Search Statistics',
  '/api/analytics/logs': 'Analytics Logs',
  '/api/analytics/history/search': 'Search History Analytics',
  '/api/analytics/cache-stats': 'Cache Statistics',
  '/api/analytics/stats/api': 'API Statistics',
  '/api/analytics/stats/errors': 'Error Statistics',
  '/api/analytics/stats/popular-searches': 'Popular Searches',
  '/api/analytics/stats/performance': 'Performance Statistics',
  '/api/analytics/system-logs': 'System Logs',

  // Feedback
  '/api/feedback/submit': 'Submit Feedback',
  '/api/feedback/list': 'Feedback List',
  '/api/feedback/stats': 'Feedback Statistics',

  // Health
  '/api/health': 'Health Check',
  '/health': 'Health Check',
};

/**
 * Returns a human-readable name for an API route path.
 * Handles dynamic segments (e.g., /api/tenant/123) by stripping IDs.
 */
export function getRouteName(route: string | undefined | null): string {
  if (!route) return 'Unknown';

  // Direct match
  if (ROUTE_NAME_MAP[route]) {
    return ROUTE_NAME_MAP[route];
  }

  // Handle dynamic segments: replace UUIDs, numeric IDs, etc.
  const normalized = route
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/{id}')
    .replace(/\/\d+/g, '/{id}');

  // Dynamic route patterns
  const dynamicPatterns: Record<string, string> = {
    '/api/tenant/{id}': 'Tenant Details',
    '/api/client/{id}': 'Client Details',
    '/api/admin/clients/{id}/users': 'Client User Management',
    '/api/admin/tenants/{id}/applications': 'Application Management',
    '/api/admin/tenants/{id}/applications/{id}/users': 'App User Management',
    '/api/search/bulk/{id}': 'Bulk Search Status',
    '/api/search/bulk/{id}/results': 'Bulk Search Results',
    '/api/upload/status/{id}': 'Upload Status',
    '/api/upload/{id}/buffer': 'Image Buffer',
    '/api/upload/{id}': 'Image Details',
    '/api/feedback/{id}': 'Feedback Details',
  };

  if (dynamicPatterns[normalized]) {
    return dynamicPatterns[normalized];
  }

  // Fallback: prettify the last meaningful segment
  const segments = route.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1] || 'Unknown';
  // If the last segment is a numeric or UUID, use the one before it
  const isId = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4})/.test(lastSegment);
  const displaySegment = isId && segments.length > 1
    ? segments[segments.length - 2]
    : lastSegment;

  return displaySegment
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
